import { useCallback, useRef, useState } from "react";

declare global {
  interface Window {
    turnstile?: {
      render: (
        container: HTMLElement,
        options: {
          sitekey: string;
          callback: (token: string) => void;
          "expired-callback"?: () => void;
          "error-callback"?: () => void;
          theme?: "light" | "dark" | "auto";
          size?: "normal" | "compact";
        }
      ) => string;
      reset: (widgetId: string) => void;
      remove: (widgetId: string) => void;
    };
  }
}

const TURNSTILE_SITE_KEY = import.meta.env.VITE_TURNSTILE_SITE_KEY as string;
const SCRIPT_ID = "cf-turnstile-script";

function loadTurnstileScript(): Promise<void> {
  return new Promise((resolve, reject) => {
    if (document.getElementById(SCRIPT_ID)) {
      if (window.turnstile) {
        resolve();
      } else {
        // Script tag exists but not loaded yet — wait
        const existing = document.getElementById(SCRIPT_ID) as HTMLScriptElement;
        existing.addEventListener("load", () => resolve());
        existing.addEventListener("error", () => reject(new Error("Failed to load Turnstile")));
      }
      return;
    }

    const script = document.createElement("script");
    script.id = SCRIPT_ID;
    script.src = "https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit";
    script.async = true;
    script.onload = () => resolve();
    script.onerror = () => reject(new Error("Failed to load Turnstile"));
    document.head.appendChild(script);
  });
}

/**
 * Renders a Cloudflare Turnstile widget via a **callback ref** so it survives
 * the login form swapping between the email/phone step and the OTP step.
 *
 * The widget lives inside the first step, which React unmounts when the user
 * advances to the OTP screen. With a mount-once effect the widget would never
 * come back when the user hits "change number / email" — the container would be
 * an empty box and the submit button would stay disabled until a full page
 * reload. Here, every time the container mounts we (re)render a fresh challenge,
 * and every time it unmounts we tear the widget down.
 */
export function useTurnstile() {
  const widgetIdRef = useRef<string | null>(null);
  const nodeRef = useRef<HTMLDivElement | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const containerRef = useCallback((node: HTMLDivElement | null) => {
    if (node) {
      nodeRef.current = node;
      setToken(null);
      setError(null);
      loadTurnstileScript()
        .then(() => {
          // Bail if the node was unmounted while the script loaded, or a widget
          // is already rendered into this container.
          if (nodeRef.current !== node || widgetIdRef.current || !window.turnstile) return;
          widgetIdRef.current = window.turnstile.render(node, {
            sitekey: TURNSTILE_SITE_KEY,
            callback: (t: string) => setToken(t),
            "expired-callback": () => setToken(null),
            "error-callback": () => setError("Doğrulama başarısız oldu, tekrar deneyin"),
            theme: "auto",
            size: "normal",
          });
        })
        .catch(() => setError("Turnstile yüklenemedi"));
    } else {
      // Container unmounted (e.g. moved to the OTP step) — remove the widget so
      // the next mount starts from a clean, freshly-challenged state.
      if (widgetIdRef.current && window.turnstile) {
        window.turnstile.remove(widgetIdRef.current);
      }
      widgetIdRef.current = null;
      nodeRef.current = null;
      setToken(null);
    }
  }, []);

  const reset = useCallback(() => {
    setToken(null);
    if (widgetIdRef.current && window.turnstile) {
      window.turnstile.reset(widgetIdRef.current);
    }
  }, []);

  return { containerRef, token, error, reset };
}
